import { tokens, useTheme } from "../../glassTheme.tsx";

// The three surface primitives from redesign/02-system-design.md §3: the
// page (ground + blobs), the card (glass, level 1) and the float (heavier
// glass, level 2 — nav, +add, chips). Sheets are opaque per the same section
// and get their own component for the same reason: nothing here hand-types
// a colour, everything reads the resolved light/dark branch of tokens.json.

export function GlassCard({
  children, style, padding, onClick,
}: {
  children?: React.ReactNode; style?: React.CSSProperties; padding?: string | number;
  onClick?: () => void;
}) {
  const { colors } = useTheme();
  const g = colors.glass.card;
  return (
    <div
      onClick={onClick}
      style={{
        background: `rgba(${colors.glass.rgb}, ${g.alpha})`,
        backdropFilter: `blur(${g.blur}px) saturate(${g.saturate})`,
        WebkitBackdropFilter: `blur(${g.blur}px) saturate(${g.saturate})`,
        border: `1px solid ${g.border}`,
        boxShadow: `inset 0 1px 0 ${g.insetTop}, ${g.shadow}`,
        borderRadius: tokens.radius.card,
        padding,
        ...style,
      }}
    >{children}</div>
  );
}

export function GlassFloat({
  children, style,
}: { children?: React.ReactNode; style?: React.CSSProperties }) {
  const { colors } = useTheme();
  const g = colors.glass.float;
  return (
    <div style={{
      background: `rgba(${colors.glass.rgb}, ${g.alpha})`,
      backdropFilter: `blur(${g.blur}px) saturate(${g.saturate})`,
      WebkitBackdropFilter: `blur(${g.blur}px) saturate(${g.saturate})`,
      border: `1px solid ${g.border}`,
      boxShadow: `inset 0 1px 0 ${g.insetTop}, inset 0 -1px 0 ${g.insetBottom}, ${g.shadow}`,
      borderRadius: tokens.radius.pill,
      ...style,
    }}>{children}</div>
  );
}

export function Sheet({
  children, style,
}: { children?: React.ReactNode; style?: React.CSSProperties }) {
  const { colors } = useTheme();
  return (
    <div style={{
      background: colors.sheet,
      border: `1px solid ${colors.hair}`,
      borderRadius: tokens.radius.sheet,
      ...style,
    }}>{children}</div>
  );
}

/**
 * Level 0: the cream ground with two soft blurred blobs, so the blur on the
 * glass above it has something to catch (§3). `position: fixed`, not a
 * pseudo-element on a `position: relative` page column — the page here is a
 * real scrolling document with no fixed height, unlike the design page's
 * phone mockups, so blobs positioned with `bottom: -15%` need a containing
 * block with a definite height to resolve against. Fixed to the viewport
 * avoids that: the ground reads as a backdrop the content scrolls over.
 */
export function Blobs() {
  const { colors } = useTheme();
  return (
    <div aria-hidden style={{
      position: "fixed", top: 0, bottom: 0, left: "50%", transform: "translateX(-50%)",
      width: "min(560px, 100vw)", overflow: "hidden", zIndex: -1,
      background: colors.ground,
    }}>
      <Blob width="70%" left="-20%" top="-8%" color={colors.blobApricot} opacity={colors.blobOpacity} />
      <Blob width="60%" right="-18%" top="35%" color={colors.blobSage} opacity={colors.blobOpacity} />
      <Blob width="55%" left="20%" bottom="-15%" color={colors.blobMauve} opacity={colors.blobOpacity * 0.89} />
    </div>
  );
}

function Blob({
  width, left, right, top, bottom, color, opacity,
}: { width: string; left?: string; right?: string; top?: string; bottom?: string; color: string; opacity: number }) {
  return (
    <span style={{
      position: "absolute", width, aspectRatio: "1", borderRadius: "50%",
      filter: "blur(40px)", background: color, opacity, left, right, top, bottom,
    }} />
  );
}
